
import {
  computed, defineComponent, onBeforeMount, reactive,
} from 'vue';
import Card from '@/core/components/Tela/Card.vue';
import SelecionarDepartamento from '@/components/Cadastros/Pessoas/Colaboradores/SelecionarDepartamento.vue';
import SelecionarFuncao from '@/components/Cadastros/Pessoas/Colaboradores/SelecionarFuncao.vue';
import SelecionarUsuario from '@/components/MeuSistema/Usuarios/SelecionarUsuario.vue';
import SelecionarUnidade from '../../Produtos/Unidades/SelecionarUnidade.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import { IColaboradorComissaoParticipante, IColaboradorComissaoUnidade } from '@/models/Entidades/Cadastros/Pessoas/Colaboradores/IColaboradorComissao';
import SelecionarColaborador from './SelecionarColaborador.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { useTelaBase } from '@/core/composables/TelaBase';
import Icone from '@/core/components/Icone.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { IColaborador } from '@/models/Entidades/Cadastros/Pessoas/Colaboradores/IColaborador';
import ServicoProduto from '@/servicos/Cadastros/Produtos/ServicoProduto';
import { IDTOCategoriaComissao } from '@/models/DTO/Cadastros/Pessoas/Colaboradores/IDTOCategoriaComissao';
import { ICategoriaProdutos } from '@/models/Entidades/Cadastros/Produtos/ICategoriaProdutos';
import ServicoCategoriaProduto from '@/servicos/Cadastros/Produtos/ServicoCategoriaProduto';
import { IDTOProduto } from '@/models/DTO/Cadastros/Produtos/IDTOProduto';
import ComissaoCategoria from './ComissaoCategoria.vue';

export default defineComponent({
  name: 'DadosColaborador',
  props: {
    colaborador: {
      type: Object as () => IColaborador,
      required: true,
    },
    departamentos: {
      type: Array as () => number[],
      required: true,
    },
    funcoes: {
      type: Array as () => number[],
      required: true,
    },
    comissoesCategorias: {
      type: Array as () => IDTOCategoriaComissao[],
      required: true,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
  },
  components: {
    Card,
    SelecionarUsuario,
    SelecionarDepartamento,
    SelecionarFuncao,
    CampoNumerico,
    SelecionarColaborador,
    SelecionarUnidade,
    MensagemSemDados,
    Icone,
    ComissaoCategoria,
  },
  emits: ['update:colaborador', 'update:departamentos', 'update:funcoes', 'update:comissoesCategorias'],
  setup(props, { emit }) {
    const servicoProduto = new ServicoProduto();
    const servicoCategoriaProduto = new ServicoCategoriaProduto();

    const {
      apresentarMensagemAlerta,
    } = useTelaBase();

    const state = reactive({
      colunasParticipantes: [] as IColumn[],
      colunasUnidades: [] as IColumn[],
      participante: {} as IColaboradorComissaoParticipante,
      unidade: {} as IColaboradorComissaoUnidade,
      indexParticipante: -1,
      indexUnidade: -1,
    });

    const computedColaborador = computed({
      get: () => props.colaborador,
      set: (valor: IColaborador) => {
        emit('update:colaborador', valor);
      },
    });

    const computedDepartamentos = computed({
      get: () => props.departamentos,
      set: (valor: number[]) => {
        emit('update:departamentos', valor);
      },
    });

    const computedFuncoes = computed({
      get: () => props.funcoes,
      set: (valor: number[]) => {
        emit('update:funcoes', valor);
      },
    });

    const computedComissoesCategorias = computed({
      get: () => props.comissoesCategorias,
      set: (valor: IDTOCategoriaComissao[]) => {
        emit('update:comissoesCategorias', valor);
      },
    });

    function preencherColunas() {
      state.colunasParticipantes = [
        {
          title: 'Codigo', dataIndex: 'codigo', key: 'Codigo', position: 0, visible: false,
        },
        {
          title: 'CodigoColaborador', dataIndex: 'codigoColaborador', key: 'CodigoColaborador', position: 1, visible: false,
        },
        {
          title: 'Colaborador', dataIndex: 'nomeColaborador', key: 'NomeColaborador', position: 2, visible: true, align: 'left',
        },
        {
          title: 'Percentual', dataIndex: 'percentualComissao', key: 'PercentualComissao', position: 3, visible: true, align: 'right', width: 150, customRenderRow: ECustomRenderRow.MascararPorcentagem,
        },
        {
          title: 'Ações', key: 'acoes', position: 4, visible: true, align: 'center', width: 70, customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
      state.colunasUnidades = [
        {
          title: 'Codigo', dataIndex: 'codigo', key: 'Codigo', position: 0, visible: false,
        },
        {
          title: 'CodigoUnidade', dataIndex: 'codigoColaborador', key: 'CodigoColaborador', position: 1, visible: false,
        },
        {
          title: 'Unidade', dataIndex: 'descricaoUnidade', key: 'DescricaoUnidade', position: 2, visible: true, align: 'left',
        },
        {
          title: 'Participante', dataIndex: 'nomeParticipante', key: 'NomeParticipante', position: 3, visible: true, align: 'left', width: 350,
        },
        {
          title: 'Valor', dataIndex: 'valorComissao', key: 'ValorComissao', position: 4, visible: true, align: 'left', width: 150, customRenderRow: ECustomRenderRow.MascararFracionado,
        },
        {
          title: 'Ações', key: 'acoes', position: 5, visible: true, align: 'center', width: 70, customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    function limparCampos() {
      state.participante = {} as IColaboradorComissaoParticipante;
      state.participante.codigoColaborador = 0;
      state.participante.percentualComissao = 0;
      state.unidade = {} as IColaboradorComissaoUnidade;
      state.unidade.codigoUnidade = 0;
      state.unidade.valorComissao = 0;
      state.indexParticipante = -1;
      state.indexUnidade = -1;
    }

    function inserirParticipante() {
      if (state.participante.codigoColaborador === 0 || state.participante.codigoColaborador === undefined) {
        apresentarMensagemAlerta('Selecione um colaborador para adicionar como participante.');
        return;
      }
      if (state.participante.percentualComissao === 0) {
        apresentarMensagemAlerta('Informe o percentual de comissão do participante.');
        return;
      }

      if (state.indexParticipante === -1) {
        if (computedColaborador.value.comissao.participantes.some((p) => p.codigoColaborador === state.participante.codigoColaborador)) {
          apresentarMensagemAlerta('Este colaborador já está adicionado como participante.');
          return;
        }

        computedColaborador.value.comissao.participantes.push(state.participante);
      } else {
        computedColaborador.value.comissao.participantes[state.indexParticipante] = state.participante;
      }
      state.participante = {} as IColaboradorComissaoParticipante;
      state.indexParticipante = -1;
    }

    function editarParticipante(index: number) {
      state.indexParticipante = index;
      state.participante = UtilitarioGeral.clonarObjeto(computedColaborador.value.comissao.participantes[index]);
    }

    function excluirParticipante(index: number) {
      computedColaborador.value.comissao.participantes.splice(index, 1);
      state.participante = {} as IColaboradorComissaoParticipante;
      state.indexParticipante = -1;
    }

    function inserirUnidade() {
      if (state.unidade.codigoUnidade === 0 || state.unidade.codigoUnidade === undefined) {
        apresentarMensagemAlerta('Selecione uma unidade para adicionar comissão para ela.');
        return;
      }
      if (state.unidade.valorComissao === 0) {
        apresentarMensagemAlerta('Informe o valor de comissão por unidade.');
        return;
      }

      if (state.indexUnidade === -1) {
        computedColaborador.value.comissao.unidades.push(state.unidade);
      } else {
        computedColaborador.value.comissao.unidades[state.indexUnidade] = state.unidade;
      }
      state.unidade = {} as IColaboradorComissaoUnidade;
      state.indexUnidade = -1;
    }

    function editarUnidade(index: number) {
      state.indexUnidade = index;
      state.unidade = UtilitarioGeral.clonarObjeto(computedColaborador.value.comissao.unidades[index]);
    }

    function excluirUnidade(index: number) {
      computedColaborador.value.comissao.unidades.splice(index, 1);
      state.unidade = {} as IColaboradorComissaoUnidade;
      state.indexUnidade = -1;
    }

    function preencherProdutosCategorias(categoria: IDTOCategoriaComissao, produtos: IDTOProduto[]) {
      if (!UtilitarioGeral.validaLista(produtos)) return;
      produtos.forEach((p) => {
        if (p.categorias.some((c) => c.codigoCategoriaProduto === categoria.codigoCategoria)) {
          const categoriaComissao = {} as IDTOCategoriaComissao;
          categoriaComissao.codigoProdutoDefinicao = p.codigoDefinicao;
          categoriaComissao.descricaoProduto = p.descricao;
          categoriaComissao.percentualComissao = 0;
          const indexComissao = props.colaborador.comissao.categorias.findIndex((c) => c.codigoProdutoDefinicao === p.codigoDefinicao);
          if (indexComissao !== -1) {
            categoriaComissao.percentualComissao = props.colaborador.comissao.categorias[indexComissao].percentualComissao;
            categoriaComissao.ativo = props.colaborador.comissao.categorias[indexComissao].ativo;
          } else {
            categoriaComissao.percentualComissao = 0;
            categoriaComissao.ativo = true;
          }
          categoria.filhos.push(categoriaComissao);
        }
      });
    }

    function preencherCategoriasFilhas(categorias: ICategoriaProdutos[], categoriaPai: IDTOCategoriaComissao, codigosExcluidos: number[], produtos: IDTOProduto[]) {
      categorias.forEach((categoria, index) => {
        if (categoria.codigoCategoriaProdutoPai === categoriaPai.codigoCategoria) {
          codigosExcluidos.push(categoria.codigo);
          const categoriaComissao = {} as IDTOCategoriaComissao;
          categoriaComissao.codigoCategoria = categoria.codigo;
          categoriaComissao.descricaoCategoria = categoria.descricao;
          categoriaComissao.filhos = [] as IDTOCategoriaComissao[];
          categoriaComissao.expandir = false;
          const indexComissao = props.colaborador.comissao.categorias.findIndex((c) => c.codigoCategoriaProduto === categoria.codigo);
          if (indexComissao !== -1) {
            categoriaComissao.percentualComissao = props.colaborador.comissao.categorias[indexComissao].percentualComissao;
            categoriaComissao.ativo = props.colaborador.comissao.categorias[indexComissao].ativo;
          } else {
            categoriaComissao.percentualComissao = 0;
            categoriaComissao.ativo = true;
          }

          preencherCategoriasFilhas(categorias, categoriaComissao, codigosExcluidos, produtos);
          preencherProdutosCategorias(categoriaComissao, produtos);
          categoriaPai.filhos.push(categoriaComissao);
        }
      });
    }

    async function preencherCategorias() {
      const categorias = await servicoCategoriaProduto.obterTodosAtivos(props.empresas[0]);
      const produtos = await servicoProduto.obterTodosAtivos(props.empresas[0]);
      const categoriasOrdenadas = [] as IDTOCategoriaComissao[];
      const codigosExcluidos = [] as number[];

      categorias.forEach((categoria, index) => {
        const categoriaComissao = {} as IDTOCategoriaComissao;
        categoriaComissao.codigoCategoria = categoria.codigo;
        categoriaComissao.descricaoCategoria = categoria.descricao;
        categoriaComissao.filhos = [] as IDTOCategoriaComissao[];
        categoriaComissao.expandir = false;

        const indexComissao = props.colaborador.comissao.categorias.findIndex((c) => c.codigoCategoriaProduto === categoria.codigo);
        if (indexComissao !== -1) {
          categoriaComissao.percentualComissao = props.colaborador.comissao.categorias[indexComissao].percentualComissao;
          categoriaComissao.ativo = props.colaborador.comissao.categorias[indexComissao].ativo;
        } else {
          categoriaComissao.percentualComissao = 0;
          categoriaComissao.ativo = true;
        }

        preencherCategoriasFilhas(categorias, categoriaComissao, codigosExcluidos, produtos);
        preencherProdutosCategorias(categoriaComissao, produtos);
        categoriasOrdenadas.push(categoriaComissao);
      });

      const categoriasFiltradas = categoriasOrdenadas.filter((c) => !codigosExcluidos.includes(c.codigoCategoria));
      computedComissoesCategorias.value = categoriasFiltradas;
    }

    onBeforeMount(async () => {
      limparCampos();
      preencherColunas();
      preencherCategorias();
    });

    return {
      props,
      state,
      ECustomRenderRow,
      computedColaborador,
      computedDepartamentos,
      computedFuncoes,
      computedComissoesCategorias,
      UtilitarioMascara,
      inserirParticipante,
      editarParticipante,
      excluirParticipante,
      inserirUnidade,
      editarUnidade,
      excluirUnidade,
    };
  },
});
